/**
 * @file useForm.js
 * @description A custom React hook for managing form state, validation, and submission.
 * This hook provides a complete form management solution including state handling,
 * validation, error management, and submission handling.
 *
 * Why this file exists:
 * 1. Provides a reusable form management solution across the application
 * 2. Reduces boilerplate code in form components
 * 3. Standardizes form validation and error handling
 * 4. Implements consistent user feedback through messages
 * 5. Makes form state management more maintainable and predictable
 */

import { useState } from "react";
import { validateForm } from "../utils/formValidation";

/**
 * Custom hook for form management
 * @param {Object} initialState - Initial state of the form fields
 * @param {Object} validationSchema - Schema for form validation
 * @param {Function} onSubmit - Function to handle form submission
 * @returns {Object} An object containing form state and handlers:
 *   - formState: Current state of the form
 *   - handleChange: Function to handle input changes
 *   - handleSubmit: Function to handle form submission
 *   - errors: Validation errors
 *   - message: Success/error message
 *   - handleCloseMessage: Function to close the message
 *   - resetForm: Function to reset the form to initial state
 *
 * Why this hook exists:
 * 1. Centralizes form state management logic
 * 2. Provides consistent form validation across the application
 * 3. Handles form submission with proper error handling
 * 4. Manages user feedback through success/error messages
 * 5. Simplifies form implementation in components
 */
const useForm = (initialState, validationSchema, onSubmit) => {
  // State management
  // Using separate states for form data, errors, and messages
  // This separation allows for independent updates and better control
  const [formState, setFormState] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({
    text: "",
    severity: "info",
    open: false,
  });

  /**
   * Handles input changes in form fields
   * @param {Event} e - The change event from the input
   *
   * Why this function exists:
   * 1. Normalizes input values based on field type
   * 2. Handles special cases like number inputs
   * 3. Updates form state while maintaining other field values
   * 4. Provides a consistent way to handle form field changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(e.target);
    let processedValue = value;

    // Special handling for number inputs to ensure proper type conversion
    if (e.target.type === "number") {
      processedValue = value === "" ? "" : value === "0" ? 0 : Number(value);
    }

    setFormState((prevState) => ({ ...prevState, [name]: processedValue }));
  };

  /**
   * Resets the form to its initial state
   *
   * Why this function exists:
   * 1. Provides a way to clear the form after successful submission
   * 2. Allows users to start fresh with the form
   * 3. Clears both form data and validation errors
   * 4. Maintains form consistency across the application
   */
  const resetForm = () => {
    setFormState(initialState);
    setErrors({});
  };

  /**
   * Handles form submission
   * @param {Event} e - The submit event
   *
   * Why this function exists:
   * 1. Validates form data before submission
   * 2. Handles the submission process asynchronously
   * 3. Provides user feedback through success/error messages
   * 4. Resets the form on successful submission
   * 5. Manages error states appropriately
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    const validationErrors = validateForm(formState, validationSchema);
    setErrors(validationErrors);

    // Only proceed with submission if there are no validation errors
    if (Object.keys(validationErrors).length === 0) {
      try {
        const response = await onSubmit(formState);
        resetForm(); // Reset the form after successful submission
        setMessage({ text: response.message, severity: "success", open: true });
      } catch (error) {
        setMessage({ text: error.message, severity: "error", open: true });
      }
    }
  };

  /**
   * Closes the message notification
   *
   * Why this function exists:
   * 1. Provides user control over message visibility
   * 2. Maintains clean UI by allowing message dismissal
   * 3. Preserves message state for potential re-display
   */
  const handleCloseMessage = () => {
    setMessage((prev) => ({ ...prev, open: false }));
  };

  return {
    formState,
    handleChange,
    handleSubmit,
    errors,
    message,
    handleCloseMessage,
    resetForm,
  };
};

export default useForm;
